import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { pageInfo } from "./pageInfo";
import { setPageContext, trackPageEntered } from "./tracking";

/* Fires PageEntered on every recognized route change and keeps the tracking
 * layer's page context current so other events (fired from non-React code) can
 * tag the page they happened on. Mounted once at the Router root, so it covers
 * the gated dashboard pages and the workspace-less gated/public pages (auth,
 * docs link, invite) alike.
 *
 * Unrecognized paths (redirects, the catch-all) resolve to no page and don't
 * fire — they're transient and not real destinations.
 *
 * Referrer = the previous in-app location; on the first page of a session it's
 * the document referrer's host (external entry point). */
export function usePageTracking(): void {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    const info = pageInfo(location.pathname);
    if (!info.page) return;

    const referrerPagePath = prevPath.current ?? undefined;
    const referrerPageHost =
      prevPath.current === null && document.referrer
        ? safeHost(document.referrer)
        : window.location.host;

    setPageContext({
      page: info.page,
      scope: info.scope,
      agentId: info.agentId,
      path: location.pathname,
      referrerPagePath,
      referrerPageHost,
    });

    trackPageEntered();

    prevPath.current = location.pathname;
  }, [location.pathname]);
}

function safeHost(url: string): string | undefined {
  try {
    return new URL(url).host;
  } catch {
    return undefined;
  }
}
